/**
 * SectorChoiceModal — blocking, single-choice sector picker.
 *
 * Used by:
 *   • Employer login flow (legacy tvang — when organizations.sector is missing)
 *
 * UX rules:
 *   • Cannot be dismissed without picking. No ESC, no overlay-click close.
 *   • Save is irreversible — explanation copy makes that clear.
 *   • 5 sector pills (matches landing-grid look) + Save button.
 *   • Light + dark friendly via Tailwind brand tokens.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, Loader2 } from 'lucide-react';
import { SECTOR_LIST } from '../config/sectors';
import { apiErrorText } from '../i18n/apiErrors';

const SectorChoiceModal = ({ open, onSaved, role = 'employer', language = 'no' }) => {
  // Reset state via `key` on the parent — open-state mapped 1:1 to component
  // identity so we don't need an effect to clear selection.
  return open ? (
    <SectorChoiceModalInner onSaved={onSaved} role={role} language={language} />
  ) : null;
};

const SectorChoiceModalInner = ({ onSaved, role, language }) => {
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const isProfessional = role === 'professional';
  const copy = language === 'en' ? {
    title: 'Choose your sector',
    body: isProfessional
      ? 'We show jobs and employers from your sector only. This choice cannot be changed later without admin support.'
      : 'We show candidates from your sector only. This choice cannot be changed later without admin support.',
    save: 'Save sector',
    saving: 'Saving…',
    required: 'Pick a sector to continue',
    locked: 'Sector is locked after first save',
  } : {
    title: 'Velg din sektor',
    body: isProfessional
      ? 'Vi viser stillinger og kommuner fra din sektor. Valget kan ikke endres senere uten admin-hjelp.'
      : 'Vi viser kandidater fra din sektor. Valget kan ikke endres senere uten admin-hjelp.',
    save: 'Lagre sektor',
    saving: 'Lagrer…',
    required: 'Velg en sektor for å fortsette',
    locked: 'Sektor låses etter første lagring',
  };

  const handleSave = async () => {
    if (!selected) {
      setError(copy.required);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const token = localStorage.getItem('access_token');
      const endpoint = isProfessional ? '/api/profiles/me/sector' : '/api/organizations/me/sector';
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}${endpoint}`,
        { sector: selected },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      onSaved?.(selected);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = apiErrorText(detail, 'Kunne ikke lagre. Prøv igjen.', 'Could not save. Please try again.');
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-ink/85 backdrop-blur-md p-4"
          data-testid="sector-choice-modal"
          // Block backdrop click — no escape.
          onClick={(e) => e.stopPropagation()}
        >
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="w-full max-w-2xl bg-paper border border border-mist overflow-hidden"
          >
            <div className="px-8 pt-8 pb-6 border-b border-mist">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-full bg-paper-warm flex items-center justify-center">
                  <Lock size={14} className="text-fjord" />
                </div>
                <span className="font-mono text-[10px] tracking-[0.3em] uppercase font-bold text-moss">
                  {copy.locked}
                </span>
              </div>
              <h2 className="font-display font-light text-3xl text-ink leading-tight">
                {copy.title}
              </h2>
              <p className="mt-3 text-sm text-moss leading-relaxed max-w-md">
                {copy.body}
              </p>
            </div>

            <div className="px-8 py-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {SECTOR_LIST.map((s) => {
                  const Icon = s.icon;
                  const isSelected = selected === s.id;
                  return (
                    <button
                      key={s.id}
                      type="button"
                      data-testid={`sector-pill-${s.id}`}
                      onClick={() => { setSelected(s.id); setError(null); }}
                      className={`flex items-center gap-3 px-5 py-4 border border-2 transition-all duration-200 text-left ${
                        isSelected
                          ? 'border-fjord bg-paper-warm'
                          : 'border-mist hover:border-moss/40 bg-paper'
                      }`}
                    >
                      <div
                        className={`w-9 h-9 rounded-xl grid place-items-center flex-shrink-0 transition-colors ${
                          isSelected ? 'bg-ink text-white' : 'bg-bone text-moss'
                        }`}
                      >
                        <Icon size={16} />
                      </div>
                      <div className="flex-1">
                        <div className={`font-display font-bold text-sm ${isSelected ? 'text-fjord' : 'text-ink'}`}>
                          {s.label}
                        </div>
                        <div className="text-[11px] text-moss/70 mt-0.5">{s.tagline}</div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {error && (
                <div
                  className="mt-4 px-4 py-2.5 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700"
                  data-testid="sector-modal-error"
                >
                  {error}
                </div>
              )}
            </div>

            <div className="px-8 pb-8">
              <button
                type="button"
                onClick={handleSave}
                disabled={!selected || saving}
                data-testid="sector-modal-save"
                className="w-full flex items-center justify-center gap-2 px-6 py-4 rounded-full font-display font-bold text-sm uppercase tracking-[0.15em] bg-ink text-white transition-all disabled:opacity-40 disabled:hover:translate-y-0 disabled:hover:shadow-none"
              >
                {saving ? <Loader2 size={16} className="animate-spin" /> : null}
                {saving ? copy.saving : copy.save}
              </button>
            </div>
          </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default SectorChoiceModal;
